import React, { useState } from "react";
import { motion } from "framer-motion";
import { GraduationCap, Dumbbell, Link2, Calendar, Clock, MapPin, Ticket, Mail, Maximize2 } from "lucide-react";
import { useLang } from "../i18n";
import { useLenis, Reveal } from "../components/motion";
import { MiniSite, CourtLines, MonoTag, GhostText, Stamp } from "../components/motifs";
import { FicheInfo } from "../components/Fiche";
import { WhatsAppShare, Lightbox } from "../components/Lightbox";
import { ContactSection } from "../components/Layout";

const TryoutsSection = ({ data, th }) => {
  const { t } = useLang();
  const [lb, setLb] = useState(null);
  if (!data.tryouts) return null;
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-10 pt-2 pb-10" data-testid="team-tryouts">
      <Reveal>
        <MonoTag style={{ color: th.accent }}>
          ✦ {t({ fr: "Tryouts · Les sélections arrivent", en: "Tryouts · Selections are coming", es: "Tryouts · Las selecciones llegan" })} ✦
        </MonoTag>
      </Reveal>
      <div className={`mt-6 grid grid-cols-1 ${data.tryouts.length > 1 ? "xl:grid-cols-2" : ""} gap-6`}>
        {data.tryouts.map((tr, i) => (
          <Reveal key={tr.flyer} delay={i * 0.1}>
            <div data-testid={`tryout-card-${i}`} className="h-full rounded-lg border border-white/10 overflow-hidden grid grid-cols-1 sm:grid-cols-5"
              style={{ backgroundColor: "var(--ms-surface)" }}>
              <button type="button" onClick={() => setLb(i)} data-testid={`tryout-flyer-${i}`}
                className="group relative sm:col-span-2 min-h-[320px] cursor-zoom-in">
                <img src={tr.flyer} alt={t(tr.title)} className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]" />
                <span className="absolute bottom-3 right-3 flex items-center gap-2 bg-black/60 backdrop-blur px-3 py-2 rounded-full font-mono text-[9px] uppercase tracking-[0.2em] text-white/85">
                  <Maximize2 size={12} /> {t({ fr: "Voir le flyer", en: "View flyer", es: "Ver el flyer" })}
                </span>
              </button>
              <div className="sm:col-span-3 p-7 md:p-8 flex flex-col">
                <h3 className="font-display text-2xl md:text-3xl uppercase leading-none">{t(tr.title)}</h3>
                <ul className="mt-6 space-y-3.5">
                  {[
                    [Calendar, tr.date], [Clock, tr.time], [MapPin, tr.place], [Ticket, tr.fee],
                  ].map(([Icon, val], k) => (
                    <li key={t(val)} className="flex items-start gap-3">
                      <Icon size={16} className="mt-0.5 shrink-0" style={{ color: th.accent }} />
                      <span className="font-body text-sm text-white/85">{t(val)}</span>
                    </li>
                  ))}
                </ul>
                <ul className="mt-5 space-y-2">
                  {tr.details.map((d, k) => (
                    <li key={d.fr} className="border-l-2 pl-3 font-body text-sm text-white/70" style={{ borderColor: th.accent }}>{t(d)}</li>
                  ))}
                </ul>
                {tr.payment && (
                  <p className="mt-5 font-mono text-[10px] leading-relaxed uppercase tracking-[0.08em] text-white/55 border border-white/10 rounded-md p-3.5">
                    {t(tr.payment)}
                  </p>
                )}
                <div className="mt-auto pt-5 flex flex-wrap items-center gap-x-6 gap-y-2">
                  {tr.contact && (
                    <a href={`mailto:${tr.contact}`} data-testid={`tryout-contact-${i}`}
                      className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] hover:underline" style={{ color: th.accent }}>
                      <Mail size={13} /> {tr.contact}
                    </a>
                  )}
                  {tr.bring && <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-white/45">{t(tr.bring)}</span>}
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      {lb !== null && (
        <Lightbox images={data.tryouts.map((x) => x.flyer)} index={lb} onIndex={setLb} onClose={() => setLb(null)} />
      )}
    </section>
  );
};

const CoachSection = ({ data, th }) => {
  const { t } = useLang();
  if (!data.coach) return null;
  const c = data.coach;
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-10 pt-2 pb-10" data-testid="team-coach">
      <Reveal>
        <div className="relative overflow-hidden rounded-lg border border-white/10 grid grid-cols-1 lg:grid-cols-12"
          style={{ backgroundColor: "#0B0E16" }}>
          <div aria-hidden className="absolute inset-0 pointer-events-none"
            style={{ background: `radial-gradient(ellipse 55% 60% at 25% -10%, ${th.accent}30, transparent 65%)` }} />
          <div className="relative lg:col-span-5 min-h-[320px]">
            <img src={c.photo} alt={c.name} className="absolute inset-0 w-full h-full object-cover object-top" />
            <div aria-hidden className="absolute inset-0 lg:bg-gradient-to-r bg-gradient-to-t from-transparent via-transparent to-transparent lg:to-[#0B0E16]" style={{ background: "linear-gradient(to right, transparent 60%, #0B0E16)" }} />
          </div>
          <div className="relative z-10 lg:col-span-7 p-8 md:p-12">
            <MonoTag style={{ color: th.accent }}>
              ★ {t({ fr: "Coach en vedette · Équipe féminine", en: "Featured coach · Women's team", es: "Coach destacado · Equipo femenino" })} ★
            </MonoTag>
            <h2 className="font-display text-4xl md:text-5xl uppercase leading-none mt-4">{c.name}</h2>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] mt-3" style={{ color: th.accent }}>{t(c.role)}</p>
            <p className="font-body text-white/80 leading-relaxed mt-6 max-w-2xl">{t(c.bio)}</p>
            <div className="flex flex-wrap gap-2.5 mt-7">
              {c.highlights.map((h, i) => (
                <span key={h.fr} className="rounded-full border border-white/20 px-4 py-2 font-mono text-[9px] uppercase tracking-[0.15em] text-white/75">
                  {t(h)}
                </span>
              ))}
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
};

// Shared mini-site template for university team pages (Sherbrooke / Legacy 3R)
export default function UniversityTeam({ data }) {
  useLenis();
  const { t } = useLang();
  const th = data.theme;
  const blocks = [
    { icon: Dumbbell, title: { fr: "Projet sportif", en: "Athletic project", es: "Proyecto deportivo" }, text: data.sport },
    { icon: GraduationCap, title: { fr: "Projet académique", en: "Academic project", es: "Proyecto académico" }, text: data.academic },
    { icon: Link2, title: { fr: "Lien universitaire", en: "University link", es: "Vínculo universitario" }, text: data.link },
  ];
  return (
    <MiniSite theme={th}>
      <section className="relative pt-32 md:pt-44 pb-16 md:pb-24 overflow-hidden grain">
        <GhostText text={data.ghost} className="text-[16vw] top-16 -left-4" />
        <CourtLines className="-right-56 -top-40 w-[640px] h-[640px]" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="flex flex-wrap items-center gap-3">
              {data.leagueLogo && (
                <img src={data.leagueLogo} alt="Prep U" data-testid="league-logo"
                  className="h-9 w-auto object-contain rounded bg-black/40 px-2 py-1 border border-white/10" />
              )}
              <MonoTag style={{ color: th.accent }}>{t(data.league)}</MonoTag>
              <MonoTag className="border border-white/25 rounded-full px-3 py-1.5 text-white/70">{data.code}</MonoTag>
            </div>
            <h1 className="font-display uppercase leading-[0.85] text-white text-5xl sm:text-6xl lg:text-8xl tracking-tight mt-5">{data.name}</h1>
            <p className="font-heading text-lg text-white/80 mt-6">{t(data.university)}</p>
            <div className="flex flex-wrap gap-x-8 gap-y-2 mt-5 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-white/60">
              <span>CAMPUS: {data.campus}</span>
              {data.staffLine && <span>RESPONSABLES: {data.staffLine}</span>}
              <span>LIGUE: PREP U</span>
              <span className="blink" style={{ color: th.accent }}>● {t({ fr: "Recrutement ouvert", en: "Recruitment open", es: "Reclutamiento abierto" })}</span>
            </div>
            <div className="mt-7">
              <WhatsAppShare testid="team-share" text={`${data.name} — Air Sports Travel`}
                label={t({ fr: "Partager", en: "Share", es: "Compartir" })} />
            </div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
            className="lg:col-span-4 flex justify-center lg:justify-end relative">
            <div className="relative">
              <div className="rounded-full p-6 bg-white/95 shadow-2xl" style={{ boxShadow: `0 0 90px ${th.accent}55` }}>
                <img src={data.logo} alt={data.name} className="w-40 h-40 md:w-52 md:h-52 object-contain" data-testid="team-logo" />
              </div>
              <Stamp top="PREP U LEAGUE" bottom={data.code} className="absolute -bottom-6 -left-8" color={th.accent} />
            </div>
          </motion.div>
        </div>
      </section>

      <TryoutsSection data={data} th={th} />

      <CoachSection data={data} th={th} />

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pt-4 pb-4">
        <Reveal><FicheInfo fiche={data.fiche} accent={th.accent} /></Reveal>
      </section>

      <section className="relative py-20 md:py-28 overflow-hidden">
        <CourtLines className="-left-60 bottom-0 w-[560px] h-[560px]" opacity={0.05} />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {blocks.map((b, i) => {
            const Icon = b.icon;
            return (
              <Reveal key={b.title.fr} delay={i * 0.1}>
                <div className="h-full rounded-md p-8 hover:-translate-y-1 transition-transform duration-300"
                  style={{ backgroundColor: "var(--ms-surface)", borderTop: `3px solid ${th.accent}` }}>
                  <Icon size={28} style={{ color: th.accent }} />
                  <h3 className="font-heading text-xl font-semibold mt-5">{t(b.title)}</h3>
                  <p className="font-body text-white/70 mt-3">{t(b.text)}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>
      <ContactSection defaultProgram={data.name} accent={th.accent} />
    </MiniSite>
  );
}
